import React, { useRef, useState } from 'react';
import { Dropdown, Badge, Modal } from 'antd';
import { Wrench as Icon, Filter as FilterIcon, Search, Star, Brain, Target, BarChart3, Shield, MessageCircle, TrendingUp, Activity, FileSearch, Globe } from "lucide-react";
import NavButton from "./ui/NavButton";
import Filter from './Filter';
import ETF_screener from './ETF_screener';
import FavoritesView from './FavoritesView';
import LLM_analysis from './LLM_analysis';
import InvestmentGoals from './Investment_Goals';
import Charts from './Charts';
import ESGCheck from './ESGCheck';
import Ask from './Ask';
import Trends from './Trends';
import SectorSummaryDashboard from './SectorSummaryDashboard';
import SectorOverview from './SectorOverview';
import BroadPicture from './BroadPicture'; // DAS-2550

const FunctionsDropdown = ({ userLoggedIn, userId }) => {
  // Create refs to access the component functions
  const filterRef = useRef();
  const etfScreenerRef = useRef();
  const favoritesRef = useRef();
  const llmRef = useRef();
  const investmentGoalsRef = useRef();
  const chartsRef = useRef();
  const esgCheckRef = useRef();
  const askRef = useRef();
  const trendsRef = useRef();
  
  // State for Sector Summary Dashboard
  const [sectorSummaryOpen, setSectorSummaryOpen] = useState(false);
  const [sectorOverviewOpen, setSectorOverviewOpen] = useState(false);
  const [broadPictureOpen, setBroadPictureOpen] = useState(false); // DAS-2550

  const handleMenuClick = ({ key }) => {
    switch (key) {
      case 'filter':
        if (filterRef.current && filterRef.current.showModal) {
          filterRef.current.showModal();
        }
        break;
      case 'etf_screener':
        if (etfScreenerRef.current && etfScreenerRef.current.showSearchModal) {
          etfScreenerRef.current.showSearchModal();
        }
        break;
      case 'favorites':
        if (favoritesRef.current && favoritesRef.current.showModal) {
          favoritesRef.current.showModal();
        }
        break;
      case 'llm':
        if (llmRef.current && llmRef.current.showModal) {
          llmRef.current.showModal();
        }
        break;
      case 'investment_goals':
        if (investmentGoalsRef.current && investmentGoalsRef.current.showModal) {
          investmentGoalsRef.current.showModal();
        }
        break;
      case 'charts':
        if (chartsRef.current && chartsRef.current.showModal) {
          chartsRef.current.showModal();
        }
        break;
      case 'esg_check':
        if (esgCheckRef.current && esgCheckRef.current.showModal) {
          esgCheckRef.current.showModal();
        }
        break;
      case 'ask':
        if (askRef.current && askRef.current.showModal) {
          askRef.current.showModal();
        }
        break;
      case 'trends':
        if (trendsRef.current && trendsRef.current.showModal) {
          trendsRef.current.showModal();
        }
        break;
      case 'sector_summary':
        setSectorSummaryOpen(true);
        break;
      case 'sector_overview':
        setSectorOverviewOpen(true);
        break;
      case 'broad_picture':
        setBroadPictureOpen(true);
        break;
      case 'portfolio_analysis':
        window.open('/portfolio_analysis', '_blank');
        break;
      default:
        break;
    }
  };

  // Expose a global opener so other components (Homepage shortcuts) can trigger the same actions
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-undef
    window.openToolsMenu = (key) => handleMenuClick({ key });
  }

  const items = [
    {
      key: 'filter',
      label: 'Filter',
      icon: <FilterIcon size={14} />
    },
    {
      key: 'etf_screener',
      label: 'ETF Screener',
      icon: <Search size={14} />
    },
    {
      key: 'favorites',
      label: 'Favorites',
      icon: <Star size={14} />
    },
    {
      key: 'llm',
      label: 'LLM',
      icon: <Brain size={14} />
    },
    {
      key: 'investment_goals',
      label: 'Investment Goals',
      icon: <Target size={14} />
    },
    {
      key: 'charts',
      label: 'Charts',
      icon: <BarChart3 size={14} />
    },
    {
      key: 'esg_check',
      label: 'ESG Check',
      icon: <Shield size={14} />
    },
    {
      key: 'ask',
      label: 'Ask',
      icon: <MessageCircle size={14} />
    },
    {
      key: 'trends',
      label: 'Company Trends',
      icon: <TrendingUp size={14} />
    },
    {
      key: 'sector_summary',
      label: 'Sector Health Check',
      icon: <Activity size={14} />
    },
    {
      key: 'sector_overview',
      label: 'Sector Overview',
      icon: <TrendingUp size={14} />
    },
    {
      key: 'broad_picture',
      label: 'Broad Picture',
      icon: <Globe size={14} />
    },
    { type: 'divider' },
    {
      key: 'portfolio_analysis',
      label: 'Portfolio Analysis',
      icon: <FileSearch size={14} />
    }
  ];

  return (
    <>
      <Dropdown
        menu={{
          items: items,
          onClick: handleMenuClick
        }}
        placement="bottomLeft"
      >
        <span data-testid="tools-menu-button">
          <NavButton
            icon={<Icon size={14} />}
            label="Tools"
            tooltip="Access to Filter, ETF Screener, Favorites, LLM, Investment Goals, Charts, ESG Check, Trends, and Sector Health Check"
          />
        </span>
      </Dropdown>

      {/* Hidden components that we can reference */}
      <div style={{ display: 'none' }}>
        <Filter ref={filterRef} disabled={!userLoggedIn} />
        <ETF_screener ref={etfScreenerRef} disabled={!userLoggedIn} />
        <FavoritesView ref={favoritesRef} disabled={!userLoggedIn} />
        <LLM_analysis ref={llmRef} disabled={!userLoggedIn} />
        <InvestmentGoals ref={investmentGoalsRef} disabled={!userLoggedIn} />
        <Charts ref={chartsRef} disabled={!userLoggedIn} />
        <ESGCheck ref={esgCheckRef} />
        <Ask ref={askRef} disabled={!userLoggedIn} />
        <Trends ref={trendsRef} disabled={!userLoggedIn} />
      </div>
      
      {/* Sector Summary Dashboard Modal */}
      <SectorSummaryDashboard 
        open={sectorSummaryOpen} 
        onClose={() => setSectorSummaryOpen(false)} 
      />

      {/* Sector Overview Modal */}
      <SectorOverview
        open={sectorOverviewOpen}
        onClose={() => setSectorOverviewOpen(false)}
      />

      {/* Broad Picture Modal (DAS-2550) */}
      <Modal
        title="🌍 Broad Picture — macro regime"
        open={broadPictureOpen}
        onCancel={() => setBroadPictureOpen(false)}
        footer={null}
        width={920}
        destroyOnClose
      >
        <BroadPicture />
      </Modal>
    </>
  );
};

export default FunctionsDropdown;
